import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useConfiguration, useUpdateTemperatureConfiguration } from "@/hooks";
import { DEFAULT_OPTIONS } from "@/lib/constants";
import { useForm } from "react-hook-form";
import {
  TemperatureConfigurationRequest,
  TemperatureConfigurationRequestSchema,
} from "@/api";
import { zodResolver } from "@hookform/resolvers/zod";

// Import Shadcn Form components
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Table,
  TableHead,
  TableHeader,
  TableCell,
  TableRow,
  TableBody,
} from "@/components/ui/table";

export function TemperatureConfiguration() {
  // query hook
  const { data: configuration, isLoading: configurationLoading } = useConfiguration();
  // mutation hooks
  const temperatureConfiguration = useUpdateTemperatureConfiguration();

  const temperatureForm = useForm<TemperatureConfigurationRequest>({
    resolver: zodResolver(TemperatureConfigurationRequestSchema),
    values: {
      bodyTemperatureC:
        configuration?.tBodyTemperatureC ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.BODY_TEMPERATURE_CELSIUS),
      bodyTemperatureShiftC:
        configuration?.tBodyTemperatureShiftC ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.BODY_TEMPERATURE_SHIFT_CELSIUS),
      temperatureRounding:
        configuration?.tTemperatureRounding ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.TEMPERATURE_ROUNDING),
      clHeavy100:
        configuration?.tClHeavy100 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_HEAVY_100),
      clHeavy75:
        configuration?.tClHeavy75 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_HEAVY_75),
      clHeavy50:
        configuration?.tClHeavy50 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_HEAVY_50),
      clHeavy25:
        configuration?.tClHeavy25 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_HEAVY_25),
      clLight100:
        configuration?.tClLight100 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_LIGHT_100),
      clLight75:
        configuration?.tClLight75 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_LIGHT_75),
      clLight50:
        configuration?.tClLight50 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_LIGHT_50),
      clLight25:
        configuration?.tClLight25 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_LIGHT_25),
      clModerate100:
        configuration?.tClModerate100 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_MODERATE_100),
      clModerate75:
        configuration?.tClModerate75 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_MODERATE_75),
      clModerate50:
        configuration?.tClModerate50 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_MODERATE_50),
      clModerate25:
        configuration?.tClModerate25 ??
        Number(DEFAULT_OPTIONS.TEMPERATURE.CL_MODERATE_25),
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuración de TGBH</CardTitle>
        <CardDescription>
          Configure los parámetros para los reportes de temperatura.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <Form {...temperatureForm}>
          <form className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={temperatureForm.control}
                name="bodyTemperatureC"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Temperatura Corporal (°C)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="0.1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Temperatura corporal de referencia normal en
                      grados Celsius.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={temperatureForm.control}
                name="bodyTemperatureShiftC"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Desplazamiento de Temperatura (°C)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        step="0.1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Rango de cambio aceptable sobre la temperatura
                      corporal normal.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            <Separator className="my-2" />
            <h3 className="text-sm font-medium">
              Configuración de Precisión
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={temperatureForm.control}
                name="temperatureRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Temperatura</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        min="0"
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores de temperatura.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>
            <Separator className="my-2" />
            <h3 className="text-sm font-medium">
              Limites máximos permisibles de exposición a condiciones
              térmicas elevadas
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <Table className="">
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <p className="text-wrap w-full">
                        Porcentaje del tiempo de exposición
                      </p>
                    </TableHead>
                    <TableHead className="w-[100px]">Ligero</TableHead>
                    <TableHead className="w-[100px]">
                      Moderado
                    </TableHead>
                    <TableHead className="w-[100px]">Pesado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="font-mono">
                  <TableRow>
                    <TableCell>100%</TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clLight100"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clModerate100"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clHeavy100"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>75%</TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clLight75"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clModerate75"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clHeavy75"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>50%</TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clLight50"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clModerate50"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clHeavy50"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>25%</TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clLight25"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clModerate25"
                        render={({ field }) => (
                          <Input
                            type="number"
                            className="w-24"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={temperatureForm.control}
                        name="clHeavy25"
                        render={({ field }) => (
                          <Input
                            type="number"
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            className="w-24"
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                onClick={temperatureForm.handleSubmit(async (data) => {
                  await temperatureConfiguration.mutateAsync(data);
                })}
                disabled={
                  configurationLoading || temperatureConfiguration.isPending
                }
                type="button"
              >
                {temperatureConfiguration.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
} 